import { normalizeDrawableGeometryType } from '@/features/Inventory/ObjectCreate/model/helpers.ts'
import type { InventoryMapObjectTypes } from '@/widgets/InventoryMap/model/types'
import {
  unwrapFullTypesList,
  type InventMapObjectTypeRow,
  type InventMapSubtypeRow,
} from '@/entities/Inventory/MapTypes/model/unwrapFullTypesResponse'

function parseStyles(raw: unknown): InventoryMapObjectTypes['styles'] {
  if (raw && typeof raw === 'object') {
    const s = raw as InventoryMapObjectTypes['styles']
    if (s.fill && s.stroke) return s
  }
  return {
    fill: 'var(--primary)',
    stroke: {
      color: 'var(--primary)',
      width: 2,
      line: { id: 0, name: '', code: 'solid' },
    },
  }
}

export function findInventTypeIdFromPassportRow(row: Record<string, unknown> | undefined): number | null {
  if (!row) return null
  const direct = Number(row.invent_objects_type_id)
  if (Number.isFinite(direct) && direct > 0) return direct

  const objectId = Number(row.id)
  if (!Number.isFinite(objectId) || objectId <= 0) return null

  const stats = row.stats as
    | {
        by_type?: Record<
          string,
          { subtypes?: Record<string, { invent_type_id?: number; ids?: number[] }> }
        >
      }
    | undefined

  for (const typeStat of Object.values(stats?.by_type ?? {})) {
    for (const sub of Object.values(typeStat.subtypes ?? {})) {
      if (Array.isArray(sub.ids) && sub.ids.includes(objectId)) {
        const tid = Number(sub.invent_type_id)
        if (Number.isFinite(tid) && tid > 0) return tid
      }
    }
  }
  return null
}

export function findSubtypeInFullTypes(
  rows: InventMapObjectTypeRow[],
  inventTypeId: number,
): { parent: InventMapObjectTypeRow; subtype: InventMapSubtypeRow } | null {
  for (const parent of rows) {
    const subtype = parent.sub_types?.find((s) => s.id === inventTypeId)
    if (subtype) return { parent, subtype }
  }
  return null
}

export function mapSubtypeToPreviewType(
  parent: InventMapObjectTypeRow,
  subtype: InventMapSubtypeRow,
): InventoryMapObjectTypes {
  const geomType = normalizeDrawableGeometryType(subtype.figure?.geom_type)
  const raw = subtype as Record<string, unknown>
  const figure = (subtype.figure ?? {}) as Record<string, unknown>
  const line = (raw.line ?? {}) as { id?: number; name?: string; code?: string }

  return {
    id: subtype.id,
    name: subtype.name,
    dependency: Number(raw.dependency ?? 0),
    functional_type: String(raw.functional_type ?? 'STANDARD') as InventoryMapObjectTypes['functional_type'],
    dependencies_list: Array.isArray(raw.dependencies_list)
      ? (raw.dependencies_list as number[])
      : [],
    figure: {
      id: Number(figure.id ?? 0),
      name: String(figure.name ?? ''),
      geom_type: geomType,
      geom_subtype: String(figure.geom_subtype ?? 'square'),
    },
    object_type: {
      id: parent.id,
      name: parent.object_name,
      icon: parent.icon,
    },
    styles: parseStyles(raw.styles),
    properties: (raw.properties ?? {}) as InventoryMapObjectTypes['properties'],
    fields: [],
    line: {
      id: Number(line.id ?? 0),
      name: String(line.name ?? ''),
      code: String(line.code ?? 'solid'),
    },
  }
}

export function buildPreviewTypeFromFullTypes(
  fullTypesData: unknown,
  inventTypeId: number,
): InventoryMapObjectTypes | null {
  const rows = unwrapFullTypesList(fullTypesData)
  const match = findSubtypeInFullTypes(rows, inventTypeId)
  if (!match) return null
  return mapSubtypeToPreviewType(match.parent, match.subtype)
}
